import { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useAppDispatch } from "@/hooks";
import { setIsApiLoading } from "@/store/appSlice";
import Breadcrumb from "@/components/common/Breadcrumb";
import Notification from "@/components/common/Notification";
import API from "@/utils/api";
import { Elements } from "@stripe/react-stripe-js";
import stripePromise from "@/lib/stripe";
import UpdateStripePaymentForm from "@/components/payment/UpdateStripePaymentForm";

interface Card {
  id: string;
  last4: string;
  brand: string;
  expMonth: number;
  expYear: number;
  isDefault?: boolean;
}

interface PaymentMethodsResponse {
  paymentMethods: Card[];
}

const SettingsPaymentCards = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [isNewCardSelected, setIsNewCardSelected] = useState(false);

  // Fetch payment methods from API
  const { data: paymentMethodsData } = useQuery<PaymentMethodsResponse>({
    queryKey: ["payment-methods"],
    queryFn: async () => {
      dispatch(setIsApiLoading(true));
      try {
        const response = await API.get("payment-methods");
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return response.data.data as PaymentMethodsResponse;
      } finally {
        dispatch(setIsApiLoading(false));
      }
    },
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  const cards = useMemo(() => paymentMethodsData?.paymentMethods ?? [], [paymentMethodsData]);

  // Set initial selected card to default card
  useEffect(() => {
    if (cards.length > 0 && !selectedCard && !isNewCardSelected) {
      const defaultCard = cards.find((card) => card.isDefault);
      if (defaultCard) {
        setSelectedCard(defaultCard);
      }
    }
  }, [cards, selectedCard, isNewCardSelected]);

  // Mutation for updating payment method
  const updatePaymentMethodMutation = useMutation({
    mutationFn: async (paymentMethodId: string) => {
      dispatch(setIsApiLoading(true));
      try {
        const response = await API.post("subscription/update-payment-method", {
          paymentMethodId,
        });
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return response.data;
      } finally {
        dispatch(setIsApiLoading(false));
      }
    },
    onSuccess: () => {
      toast.success("支払い方法が更新されました");
      // Refetch payment methods list
      void queryClient.invalidateQueries({ queryKey: ["payment-methods"] });
      // Reset form state
      setIsNewCardSelected(false);
      setSelectedCard(null);
    },
    onError: (error: unknown) => {
      console.error("Failed to update payment method:", error);
      toast.error("エラーが発生しました。もう一度お試しください。");
    },
  });

  /**
   * Get SVG path for card brand
   * @param {string} brand - Card brand (visa, mastercard, amex, jcb)
   * @returns {string} Path to SVG file
   */
  const getCardBrandSvg = (brand: string): string => {
    const brandMap: Record<string, string> = {
      visa: "/images/payment/visa.svg",
      mastercard: "/images/payment/mastercard.svg",
      amex: "/images/payment/amex.svg",
      jcb: "/images/payment/jcb.svg",
    };
    return brandMap[brand.toLowerCase()] || brandMap.visa;
  };

  useEffect(() => {
    function handleScroll() {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 100);
    }

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial state

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleAddNewPaymentMethod = (paymentMethodId: string) => {
    void updatePaymentMethodMutation.mutateAsync(paymentMethodId);
  };

  return (
    <>
      <Helmet>
        <title>契約・支払情報</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-custom">
        {/* Header */}
        <div
          className={`${isScrolled ? "fixed top-0 left-0 right-0 z-[9] scrolled bg-gradient-custom" : "relative"} py-5 transition-all duration-300`}
        >
          <div className="mx-auto px-5 lg:px-10 relative max-w-5xl">
            <div className="flex items-center justify-between">
              <Breadcrumb
                items={[
                  { label: "ホーム", url: "/home" },
                  { label: "各種設定", url: "/settings" },
                  { label: "音声スピード設定" },
                ]}
              />
              <div className="flex items-center gap-4">
                <div
                  className="relative cursor-pointer hidden lg:block"
                  onClick={() => {
                    void navigate("/wordbook");
                  }}
                >
                  <img src="/images/card_icon.svg" alt="Card" />
                </div>
                <Notification />
                <div
                  className="relative cursor-pointer hidden lg:block"
                  onClick={() => {
                    void navigate("/profile");
                  }}
                >
                  <img src="/images/user_icon.svg" alt="Profile" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Content - All sections vertically */}
        <div className={`${isScrolled ? "pt-[80px]" : "pt-[80px]"} pb-32 max-w-[500px] mx-auto`}>
          <div className="text-white text-xl font-bold flex justify-center items-cente mb-4">
            登録カード
          </div>
          <div className="mb-8 bg-white rounded-xl p-10">
            <div className="flex flex-col gap-2.5">
              {cards.map((card) => (
                <div key={card.id} className="flex-shrink-0">
                  {card.id === selectedCard?.id ? (
                    <div className="p-1 rounded-lg bg-gradient-to-r from-[#1bd3cc] to-[#2a51d6]">
                      <label className="flex cursor-pointer gap-4 px-4 py-4 transition bg-white rounded-[5px] items-center">
                        <input
                          type="radio"
                          name="speech-speed"
                          value={card.id}
                          className="sr-only"
                          checked={card.id === selectedCard?.id}
                          onChange={() => {
                            setSelectedCard(card);
                            setIsNewCardSelected(false);
                          }}
                        />
                        <div className="mt-1 flex-shrink-0">
                          <div className="relative h-5 w-5">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle cx="10" cy="10" r="9" fill="url(#gradient-speed)" />
                              <path
                                d="M6 10L9 13L14 7"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <defs>
                                <linearGradient
                                  id="gradient-speed"
                                  x1="0.131117"
                                  y1="-2.5"
                                  x2="18.0581"
                                  y2="-2.4597"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stopColor="#1BD3CC" />
                                  <stop offset="1" stopColor="#2A51D6" />
                                </linearGradient>
                              </defs>
                            </svg>
                          </div>
                        </div>
                        <span className="text-base font-figtree flex-1">
                          **** **** **** {card.last4}
                          <br />
                          <span>
                            {card.expMonth}/{card.expYear}
                          </span>
                        </span>
                        <img
                          src={getCardBrandSvg(card.brand)}
                          alt={card.brand}
                          className="h-full w-auto"
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="flex cursor-pointer gap-4 px-4 py-3 transition border border-[#C8C8C8] rounded-[5px] items-center">
                      <input
                        type="radio"
                        name="speech-speed"
                        value={card.id}
                        className="sr-only"
                        checked={card.id === selectedCard?.id}
                        onChange={() => {
                          setSelectedCard(card);
                          setIsNewCardSelected(false);
                        }}
                      />
                      <div className="mt-1 flex-shrink-0">
                        <div className="h-5 w-5 rounded-full border border-[#C8C8C8]"></div>
                      </div>
                      <span className="text-base font-figtree flex-1">
                        **** **** **** {card.last4}
                        <br />
                        <span>
                          {card.expMonth}/{card.expYear}
                        </span>
                      </span>
                      <img
                        src={getCardBrandSvg(card.brand)}
                        alt={card.brand}
                        className="h-full w-auto"
                      />
                    </label>
                  )}
                </div>
              ))}
              {/* Add new card option: 別のカードを使用 */}
              <div className="flex-shrink-0">
                {isNewCardSelected ? (
                  <div className="p-1 rounded-lg bg-gradient-to-r from-[#1bd3cc] to-[#2a51d6]">
                    <label className="flex cursor-pointer gap-4 px-4 py-4 transition bg-white rounded-[5px] items-center">
                      <input
                        type="radio"
                        name="speech-speed"
                        value="new-card"
                        className="sr-only"
                        checked={isNewCardSelected}
                        onChange={() => setIsNewCardSelected(true)}
                      />
                      <div className="mt-1 flex-shrink-0">
                        <div className="relative h-5 w-5">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle cx="10" cy="10" r="9" fill="url(#gradient-speed-new)" />
                            <path
                              d="M6 10L9 13L14 7"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <defs>
                              <linearGradient
                                id="gradient-speed-new"
                                x1="0.131117"
                                y1="-2.5"
                                x2="18.0581"
                                y2="-2.4597"
                                gradientUnits="userSpaceOnUse"
                              >
                                <stop stopColor="#1BD3CC" />
                                <stop offset="1" stopColor="#2A51D6" />
                              </linearGradient>
                            </defs>
                          </svg>
                        </div>
                      </div>
                      <span className="text-base font-semibold font-figtree flex-1">
                        別のカードを使用
                      </span>
                    </label>
                  </div>
                ) : (
                  <label className="flex cursor-pointer gap-4 px-4 py-3 transition border border-[#C8C8C8] rounded-[5px] items-center">
                    <input
                      type="radio"
                      name="speech-speed"
                      value="new-card"
                      className="sr-only"
                      checked={isNewCardSelected}
                      onChange={() => {
                        setIsNewCardSelected(true);
                        setSelectedCard(null);
                      }}
                    />
                    <div className="mt-1 flex-shrink-0">
                      <div className="h-5 w-5 rounded-full border border-[#C8C8C8]"></div>
                    </div>
                    <span className="text-base font-semibold font-figtree flex-1">
                      別のカードを使用
                    </span>
                  </label>
                )}
              </div>
              <div>
                {isNewCardSelected ? (
                  <>
                    <div className="flex flex-col items-start w-full my-5">
                      <p className="text-text-main font-sans text-base font-medium leading-[22px] tracking-[0.42px] mb-2.5">
                        対応クレジットカード
                      </p>
                      <img src="/images/payment.svg" alt="Payment methods" className="mb-2.5" />
                      <label className="text-text-main font-sans text-xs font-normal leading-[22px] tracking-[0.1px]">
                        ※お支払いは1回払いのみとなります
                      </label>
                    </div>
                    <Elements stripe={stripePromise}>
                      <UpdateStripePaymentForm
                        onSubmit={handleAddNewPaymentMethod}
                        isLoading={false}
                        error={undefined}
                      />
                    </Elements>
                  </>
                ) : (
                  <button
                    className="mt-4 mx-auto flex w-[80%] items-center justify-center rounded-full bg-gradient-to-r from-gradient-start to-gradient-end px-6 py-3 font-sans text-lg font-semibold text-white shadow-md transition hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => {
                      handleAddNewPaymentMethod(selectedCard?.id ?? "");
                    }}
                  >
                    変更する
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPaymentCards;
